import React, { useRef, useEffect, useState, useMemo, forwardRef, type ReactNode } from "react";
import { useNotch } from "../context/NotchContext";

interface CutoutMaskContainerProps {
  children: React.ReactNode;
  cutoutNotchWidth?: number; // Effectively unused with new SVG path logic
  cutoutNotchHeight?: number; // Effectively unused with new SVG path logic
  cutoutCornerRadius?: number; // Effectively unused with new SVG path logic
  cutoutOffset?: { x: number; y: number }; // offset from bottom right in px
  showCutout?: boolean; // controls whether the notch is open (true) or closed (false)
  animationDurationMs?: number;
  className?: string; // Tailwind classes for background, etc.
  debug?: boolean; // If true, render the SVG cutout directly for debugging
  // New props for granular control over notch geometry
  outerCurveRadius?: number;
  innerCurveRadius?: number;
  verticalConnectorLength?: number;
  horizontalConnectorLength?: number;
  simpleCornerRadius?: number; // Radius for the simple corner state
  morphProgress?: number; // 0 (complex notch) to 1 (simple corner)
}

const DEFAULT_OFFSET = { x: 0, y: 0 };
const DEFAULT_ANIMATION_DURATION = 400;

// Defaults for the new geometry props, derived from original SVG
const DEFAULT_OUTER_CURVE_RADIUS = 15.38; // Avg of SVG's 17.81 and 12.95
const DEFAULT_INNER_CURVE_RADIUS = 21.99;
const DEFAULT_VERTICAL_CONNECTOR_LENGTH = 47.2;
const DEFAULT_HORIZONTAL_CONNECTOR_LENGTH = 55.06;
const DEFAULT_SIMPLE_CORNER_RADIUS = 20; // Example default
const DEFAULT_MORPH_PROGRESS = 0;

const KAPPA = 0.5522847498; // For Bézier curve approximation of a quarter circle

// Linear interpolation helper
const lerp = (start: number, end: number, t: number): number => {
  return start * (1 - t) + end * t;
};

function encodeSVG(svg: string) {
  return svg
    .replace(/\\n/g, "")
    .replace(/\\t/g, " ")
    .replace(/\\s+/g, " ")
    .replace(/'/g, "%27")
    .replace(/#/g, "%23")
    .replace(/</g, "%3C")
    .replace(/>/g, "%3E");
}

export const CutoutMaskContainer = forwardRef<
  HTMLDivElement,
  CutoutMaskContainerProps
>(
  (
    {
      children,
      // cutoutNotchWidth, // Effectively unused
      // cutoutNotchHeight, // Effectively unused
      // cutoutCornerRadius, // Effectively unused
      cutoutOffset = DEFAULT_OFFSET,
      showCutout = true,
      animationDurationMs = DEFAULT_ANIMATION_DURATION,
      className = "",
      debug = false,
      outerCurveRadius = DEFAULT_OUTER_CURVE_RADIUS,
      innerCurveRadius = DEFAULT_INNER_CURVE_RADIUS,
      verticalConnectorLength = DEFAULT_VERTICAL_CONNECTOR_LENGTH,
      horizontalConnectorLength = DEFAULT_HORIZONTAL_CONNECTOR_LENGTH,
      simpleCornerRadius = DEFAULT_SIMPLE_CORNER_RADIUS,
      morphProgress = DEFAULT_MORPH_PROGRESS,
    },
    forwardedRef
  ) => {
    const internalContainerRef = useRef<HTMLDivElement>(null);
    const { setIsNotchActive } = useNotch();

    useEffect(() => {
      if (typeof forwardedRef === "function") {
        forwardedRef(internalContainerRef.current);
      } else if (forwardedRef) {
        forwardedRef.current = internalContainerRef.current;
      }
      return () => {
        if (typeof forwardedRef === "function") {
          forwardedRef(null);
        }
      };
    }, [forwardedRef]);

    const [size, setSize] = useState({ width: 0, height: 0 });
    const [animationProgress, setAnimationProgress] = useState(
      showCutout ? 1 : 0
    );
    const animationFrameId = useRef<number | null>(null);

    useEffect(() => {
      const currentDivElement = internalContainerRef.current;
      const updateSize = () => {
        if (currentDivElement) {
          const { width, height } = currentDivElement.getBoundingClientRect();
          // Round the dimensions to prevent excessive updates from sub-pixel changes
          setSize({ width: Math.round(width), height: Math.round(height) });
        }
      };

      // Initial size update
      updateSize();

      // Use ResizeObserver to detect size changes of the container itself
      let resizeObserver: ResizeObserver | null = null;
      if (currentDivElement) {
        resizeObserver = new ResizeObserver(() => {
          updateSize();
        });
        resizeObserver.observe(currentDivElement);
      }

      // Cleanup
      return () => {
        if (currentDivElement && resizeObserver) {
          resizeObserver.unobserve(currentDivElement);
        }
      };
    }, [children]); // Keep children as a dependency, as content change might affect size too.

    useEffect(() => {
      const targetProgress = showCutout ? 1 : 0;

      // If already at the target, animation is complete or not needed.
      if (animationProgress === targetProgress) {
        // Ensure any lingering animation frame is cleared.
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null;
        }
        return;
      }

      // If we are not at the target, an animation is needed.
      const startValue = animationProgress;
      let startTime: number | null = null;

      const animate = (timestamp: number) => {
        if (!startTime) {
          startTime = timestamp;
        }
        const elapsedTime = timestamp - startTime;
        let linearProgress = 0;
        if (animationDurationMs > 0) {
          linearProgress = Math.min(elapsedTime / animationDurationMs, 1);
        } else {
          linearProgress = 1; // If duration is 0, complete immediately
        }

        const newValue =
          startValue + (targetProgress - startValue) * linearProgress;

        if (linearProgress < 1) {
          setAnimationProgress(newValue);
          animationFrameId.current = requestAnimationFrame(animate);
        } else {
          setAnimationProgress(targetProgress); // Ensure it lands exactly on the target
          animationFrameId.current = null; // Animation finished
        }
      };

      // Cancel any previously ongoing animation frame before starting a new one.
      // This handles cases where dependencies change mid-animation.
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      // Start the animation
      animationFrameId.current = requestAnimationFrame(animate);

      return () => {
        if (animationFrameId.current) {
          cancelAnimationFrame(animationFrameId.current);
          animationFrameId.current = null; // Crucial for cleanup
        }
      };
    }, [showCutout, animationDurationMs, animationProgress]); // Rerun if showCutout, duration, or current progress changes

    const { width, height } = size;
    const offsetX = cutoutOffset.x || 0;
    const offsetY = cutoutOffset.y || 0;

    const effWidth = width - offsetX;
    const effHeight = height - offsetY;

    const rectPath = useMemo(() => {
      return width && height ? `M0,0 H${width} V${height} H0 Z` : "";
    }, [width, height]);

    // baseNotchGfx holds the notch geometry at scaleFactor=1 and animP=1
    const baseNotchGfx = useMemo(() => {
      const morphP = Math.max(0, Math.min(1, morphProgress)); // Clamp morphProgress to 0-1

      // Interpolate primary radii and lengths
      const Ro_eff = lerp(outerCurveRadius, simpleCornerRadius, morphP);
      const Ri_eff = lerp(innerCurveRadius, 0, morphP);
      const Lv_eff = lerp(verticalConnectorLength, 0, morphP);
      const Lh_eff = lerp(horizontalConnectorLength, 0, morphP);

      const c_inner_params = {
        cp1x: 0,
        cp1y: -Ri_eff * KAPPA,
        cp2x: Ri_eff * (1 - KAPPA),
        cp2y: -Ri_eff,
        endX: Ri_eff,
        endY: -Ri_eff,
      };
      const c_outer_bottom_params = {
        cp1x: Ro_eff * KAPPA,
        cp1y: 0,
        cp2x: Ro_eff,
        cp2y: -Ro_eff * (1 - KAPPA),
        endX: Ro_eff,
        endY: -Ro_eff,
      };

      // For the top-right curve, scale its components by (1-morphP) so it vanishes at morphP=1
      const tr_scale = 1 - morphP;
      const c_outer_top_right_params = {
        cp1x: Ro_eff * KAPPA * tr_scale,
        cp1y: 0, // Remains 0
        cp2x: Ro_eff * tr_scale,
        cp2y: -Ro_eff * (1 - KAPPA) * tr_scale,
        endX: Ro_eff * tr_scale,
        endY: -Ro_eff * tr_scale,
      };

      // Calculate dimensions for the complex notch (morphP=0)
      const complexWidth =
        2 * outerCurveRadius + horizontalConnectorLength + innerCurveRadius;
      const complexHeight =
        2 * outerCurveRadius + verticalConnectorLength + innerCurveRadius;

      // Interpolate overall bounding box dimensions
      const finalTotalWidth = lerp(complexWidth, simpleCornerRadius, morphP);
      const finalTotalHeight = lerp(complexHeight, simpleCornerRadius, morphP);

      return {
        // Store interpolated primitives to be used in path string generation
        _Ro_eff: Ro_eff,
        _Ri_eff: Ri_eff,
        _Lv_eff: Lv_eff,
        _Lh_eff: Lh_eff,
        _c_inner: c_inner_params,
        _c_outer_bottom: c_outer_bottom_params,
        _c_outer_top_right: c_outer_top_right_params,

        // These are for contentScaleFactor calculation and defining the leftmost point of the feature
        totalNotchWidthFromBR: Math.max(0, finalTotalWidth), // Ensure non-negative
        totalNotchHeightFromBR: Math.max(0, finalTotalHeight), // Ensure non-negative
      };
    }, [
      outerCurveRadius,
      innerCurveRadius,
      verticalConnectorLength,
      horizontalConnectorLength,
      simpleCornerRadius,
      morphProgress,
    ]);

    const animP = animationProgress;

    // Define a threshold for when animP is considered effectively zero
    const ANIMATION_EFFECTIVELY_ZERO_THRESHOLD = 1e-6;
    const isCutoutVisibleOrAnimating =
      animP > ANIMATION_EFFECTIVELY_ZERO_THRESHOLD;

    // Update NotchContext when isCutoutVisibleOrAnimating changes
    useEffect(() => {
      // console.log(
      //   "[CutoutMaskContainer] Calling setIsNotchActive with:",
      //   isCutoutVisibleOrAnimating
      // );
      setIsNotchActive(isCutoutVisibleOrAnimating);
    }, [isCutoutVisibleOrAnimating, setIsNotchActive]);

    // Calculate contentScaleFactor for responsiveness
    const contentScaleFactor = useMemo(() => {
      let scale = 1;
      if (
        baseNotchGfx.totalNotchWidthFromBR > 0 &&
        baseNotchGfx.totalNotchHeightFromBR > 0 &&
        effWidth > 0 &&
        effHeight > 0
      ) {
        const scaleX = effWidth / baseNotchGfx.totalNotchWidthFromBR;
        const scaleY = effHeight / baseNotchGfx.totalNotchHeightFromBR;
        scale = Math.min(1, scaleX, scaleY);
      }
      return scale;
    }, [baseNotchGfx, effWidth, effHeight]);

    const effectiveScale = useMemo(() => {
      return contentScaleFactor * animP;
    }, [contentScaleFactor, animP]);

    const gfxForPath = baseNotchGfx; // Use the structure from baseNotchGfx

    // Path for the HOLE, coordinates relative to container's bottom-right (effWidth, effHeight)
    // Dimensions are from baseNotchGfx, scaled by contentScaleFactor, then by animP.
    // Note: animP is now effectively combined with contentScaleFactor for the final drawn size.
    // If animP = 0, contentScaleFactor was set to 0, so all dimensions become 0.
    const notchPathDefinition = useMemo(() => {
      return width && height && isCutoutVisibleOrAnimating // Path only if animating open
        ? `M ${effWidth},${effHeight}` +
            ` L ${
              effWidth - gfxForPath.totalNotchWidthFromBR * effectiveScale
            },${effHeight}` +
            ` c ${gfxForPath._c_outer_bottom.cp1x * effectiveScale},${
              gfxForPath._c_outer_bottom.cp1y * effectiveScale
            }` +
            ` ${gfxForPath._c_outer_bottom.cp2x * effectiveScale},${
              gfxForPath._c_outer_bottom.cp2y * effectiveScale
            }` +
            ` ${gfxForPath._c_outer_bottom.endX * effectiveScale},${
              gfxForPath._c_outer_bottom.endY * effectiveScale
            }` +
            ` v ${-gfxForPath._Lv_eff * effectiveScale}` +
            ` c ${gfxForPath._c_inner.cp1x * effectiveScale},${
              gfxForPath._c_inner.cp1y * effectiveScale
            }` +
            ` ${gfxForPath._c_inner.cp2x * effectiveScale},${
              gfxForPath._c_inner.cp2y * effectiveScale
            }` +
            ` ${gfxForPath._c_inner.endX * effectiveScale},${
              gfxForPath._c_inner.endY * effectiveScale
            }` +
            ` h ${gfxForPath._Lh_eff * effectiveScale}` +
            ` c ${gfxForPath._c_outer_top_right.cp1x * effectiveScale},${
              gfxForPath._c_outer_top_right.cp1y * effectiveScale
            }` +
            ` ${gfxForPath._c_outer_top_right.cp2x * effectiveScale},${
              gfxForPath._c_outer_top_right.cp2y * effectiveScale
            }` +
            ` ${gfxForPath._c_outer_top_right.endX * effectiveScale},${
              gfxForPath._c_outer_top_right.endY * effectiveScale
            }` +
            ` L ${effWidth},${effHeight}` +
            ` Z`
        : "";
    }, [width, height, animP, effWidth, effHeight, gfxForPath, effectiveScale]);

    const combinedPath = useMemo(() => {
      return rectPath + " " + notchPathDefinition;
    }, [rectPath, notchPathDefinition]);

    const svgRaw = useMemo(() => {
      return width && height && isCutoutVisibleOrAnimating // Generate SVG only if notch is opening/open and container has size
        ? `<svg xmlns='http://www.w3.org/2000/svg' width='${width}' height='${height}'>` +
            `<path fill='${
              debug ? "red" : "white"
            }' fill-rule='evenodd' d='${combinedPath}'/></svg>`
        : "";
    }, [width, height, animP, debug, combinedPath]);

    const svgMaskUrl = useMemo(() => {
      return svgRaw
        ? `url("data:image/svg+xml;utf8,${encodeSVG(svgRaw)}")`
        : undefined; // Undefined if no SVG (e.g. animP = 0, notch closed)
    }, [svgRaw]);

    // Fallback CSS clip-path (approximates the notch)
    const clipPathFallback = useMemo(() => {
      return width && height && isCutoutVisibleOrAnimating
        ? `polygon(0% 0%, ${width}px 0%, ` +
            `${width}px ${
              effHeight - gfxForPath.totalNotchHeightFromBR * effectiveScale
            }px, ` +
            `${
              effWidth -
              (gfxForPath.totalNotchWidthFromBR - gfxForPath._Ro_eff) *
                effectiveScale
            }px ${
              effHeight -
              (gfxForPath.totalNotchHeightFromBR - gfxForPath._Ro_eff) *
                effectiveScale
            }px, ` +
            `${
              effWidth -
              (gfxForPath.totalNotchWidthFromBR - gfxForPath._Ro_eff) *
                effectiveScale
            }px ${effHeight - gfxForPath._Ro_eff * effectiveScale}px, ` +
            `${
              effWidth - gfxForPath.totalNotchWidthFromBR * effectiveScale
            }px ${effHeight}px, ` +
            `0% ${height}px)`
        : `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`;
    }, [width, height, animP, effWidth, effHeight, gfxForPath, effectiveScale]);

    return (
      <div
        ref={internalContainerRef}
        className={className}
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          WebkitMaskImage: svgMaskUrl,
          maskImage: svgMaskUrl,
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          // Mask image is full size and handles positioning internally
          WebkitMaskSize: "auto", // or "100% 100%"
          maskSize: "auto", // or "100% 100%"
          WebkitMaskPosition: "0% 0%", // or "auto auto"
          maskPosition: "0% 0%", // or "auto auto"
          WebkitClipPath:
            !svgMaskUrl && clipPathFallback ? clipPathFallback : undefined,
          clipPath:
            !svgMaskUrl && clipPathFallback ? clipPathFallback : undefined,
          transition: `mask-image ${animationDurationMs}ms, -webkit-mask-image ${animationDurationMs}ms, clip-path ${animationDurationMs}ms, -webkit-clip-path ${animationDurationMs}ms`,
          transform: "translateZ(0)",
        }}
      >
        <div className="w-full h-full relative bg-white dark:bg-slate-800/90">
          {/* Subtle border highlights */}
          <div className="absolute inset-x-0 top-0 h-[1px] pointer-events-none bg-gradient-to-r from-transparent via-white/40 to-transparent dark:via-white/20" />
          <div className="absolute inset-x-0 bottom-0 h-[1px] pointer-events-none bg-gradient-to-r from-transparent via-black/10 to-transparent dark:via-white/10" />

          {children}
        </div>
        {debug && width && height && (
          <svg
            width={width} // Debug SVG is also full size
            height={height}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              opacity: 0.7,
              zIndex: 50,
            }}
          >
            {/* Debug SVG uses the combined path to show the evenodd effect */}
            <path
              d={combinedPath}
              fill={
                isCutoutVisibleOrAnimating
                  ? "rgba(0, 255, 0, 0.5)"
                  : "rgba(255, 0, 0, 0.5)"
              }
              fillRule="evenodd"
            />
          </svg>
        )}
      </div>
    );
  }
);

export default CutoutMaskContainer;
